// Browser → desktop helper local API client.
//
// Two-port discovery + paired flow per ADR-017 / ADR-018:
//   1. POST http://127.0.0.1:7831/v1/discover with a backend-issued discovery
//      token. Helper returns the ephemeral port + a per-process challenge.
//   2. Subsequent paired calls go to http://127.0.0.1:<ephemeralPort>/v1/...
//      with `Authorization: Bearer <bearer>` and (for mutating requests)
//      `X-AIAssist-CSRF: <csrfToken>`. The browser auto-adds Origin.
//
// Errors are surfaced as `HelperError` with the helper's RFC-7807-style
// problem detail when present.

export const DISCOVERY_PORT = 7831;
export const DISCOVERY_BASE = `http://127.0.0.1:${DISCOVERY_PORT}`;

export interface DiscoveryResponse {
  port: number;
  challenge: string;
  helper_version: string;
  process_id: number;
}

export interface HelperStatus {
  version: string;
  paired: boolean;
  data_region: string;
  indexing: {
    active: boolean;
    pct_complete: number;
    files_indexed: number;
    files_pending: number;
    files_errored: number;
  };
}

export interface DirectoryDescriptor {
  id: string;
  path: string;
  permission: string;
  registered_at: string;
  last_indexed_at: string | null;
  status: string;
}

export interface DirectoriesResponse {
  directories: DirectoryDescriptor[];
}

export interface IndexDirectoryResponse {
  indexed: number;
  unchanged: number;
  skipped: number;
  errored: number;
  total_chunks: number;
}

export interface RegisterDirectoryRequest {
  path: string;
  permission?: 'read' | 'reference' | 'write' | 'none';
  trust_origin?: 'user-authored' | 'downloaded' | 'shared-by-third-party' | 'meeting-visual';
}

export interface SearchHit {
  chunk_id: string;
  file_id: string;
  directory_id: string;
  relative_path: string;
  score: number;
  trust_origin: string;
}

export interface SearchResponse {
  hits: SearchHit[];
}

export interface SearchRequest {
  query: string;
  directories?: string[];
  k?: number;
}

export interface ExcerptResponse {
  file_id: string;
  relative_path: string;
  byte_start: number;
  byte_end: number;
  text: string;
}

export interface AskRequest {
  question: string;
  directories?: string[];
  k?: number;
}

export interface AnswerCitation {
  chunk_id: string;
  file_id: string;
  directory_id: string;
  relative_path: string;
  byte_start: number;
  byte_end: number;
  score: number;
  trust_origin: string;
  excerpt: string;
}

export interface AnswerResponse {
  text: string;
  citations: AnswerCitation[];
  model: string;
}

/**
 * Server-sent event from /v1/answer/stream. Mirrors the Rust enum
 * `AnswerEvent` with `tag = "type"`.
 */
export type AnswerEvent =
  | { type: 'citations'; citations: AnswerCitation[]; model: string }
  | { type: 'delta'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

export interface HelperProblem {
  type: string;
  title: string;
  status: number;
  detail?: string;
}

export class HelperError extends Error {
  readonly status: number;
  readonly problem: HelperProblem | null;
  constructor(status: number, problem: HelperProblem | null, message?: string) {
    super(message ?? problem?.title ?? `helper request failed: ${status}`);
    this.status = status;
    this.problem = problem;
  }
}

/**
 * Discover the helper's ephemeral port. Pass a backend-issued discovery token
 * (today the helper accepts any non-empty string; tightening lands when
 * BE-009 ships the mint endpoint).
 */
export async function discoverHelper(
  discoveryToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DiscoveryResponse> {
  const res = await fetchImpl(`${DISCOVERY_BASE}/v1/discover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ discovery_token: discoveryToken }),
  });
  if (!res.ok) throw await asHelperError(res);
  return (await res.json()) as DiscoveryResponse;
}

export interface HelperClientOptions {
  /** e.g. `http://127.0.0.1:54321` returned by `/v1/discover`. */
  baseUrl: string;
  bearer: string;
  /** Required for POST/PUT/DELETE. */
  csrfToken: string;
  fetchImpl?: typeof fetch;
  /**
   * Called when the client recovers from a stale-port fetch failure by
   * re-running `/v1/discover` and finding a different ephemeral port.
   * Callers should persist the new port so subsequent client instances
   * use it directly. Optional; omit if the caller doesn't care.
   */
  onPortChanged?: (port: number) => void;
}

export interface TranscriptListItem {
  id: string;
  source_url: string;
  started_at_ms: number;
  ended_at_ms: number;
  line_count: number;
  has_summary: boolean;
}

export interface TranscriptListResponse {
  transcripts: TranscriptListItem[];
}

export interface TranscriptResponse {
  id: string;
  source_url: string;
  started_at_ms: number;
  ended_at_ms: number;
  line_count: number;
  /** JSON-encoded array of `{ text, at, source, speaker? }`. */
  lines_json: string;
  summary_md?: string;
  summary_model?: string;
  summary_at_ms?: number;
}

export interface TranscriptSummaryResponse {
  id: string;
  summary_md: string;
  model: string;
}

export interface HelperBillingStatusPayload {
  has_perpetual_license: boolean;
  license_purchased_at_ms: number | null;
  sub_tier: string;
  sub_current_period_end_ms: number | null;
  trial_started_at_ms: number | null;
}

export interface HelperBillingStatus {
  has_perpetual_license: boolean;
  license_purchased_at_ms: number | null;
  sub_tier: string;
  sub_current_period_end_ms: number | null;
  trial_started_at_ms: number | null;
  last_synced_at_ms: number | null;
  trial_days_remaining: number;
  cache_fresh: boolean;
}

export interface HelperCloudCredentialsPayload {
  backend_url: string;
  access_token: string;
  expires_at_ms: number;
}

export interface HelperCloudCredentialsStatus {
  configured: boolean;
  backend_url: string | null;
  expires_at_ms: number | null;
  updated_at_ms: number | null;
}

export interface HelperClient {
  status(): Promise<HelperStatus>;
  listDirectories(): Promise<DirectoriesResponse>;
  registerDirectory(req: RegisterDirectoryRequest): Promise<IndexDirectoryResponse>;
  deleteDirectory(id: string): Promise<void>;
  reindexDirectory(id: string): Promise<IndexDirectoryResponse>;
  search(req: SearchRequest): Promise<SearchResponse>;
  excerpt(fileId: string, chunkId: string): Promise<ExcerptResponse>;
  ask(req: AskRequest): Promise<AnswerResponse>;
  listTranscripts(): Promise<TranscriptListResponse>;
  getTranscript(id: string): Promise<TranscriptResponse>;
  deleteTranscript(id: string): Promise<void>;
  summarizeTranscript(id: string, force?: boolean): Promise<TranscriptSummaryResponse>;
  /**
   * Push the backend's billing status into the helper's local cache. Used
   * after pairing + after every successful Stripe checkout so the helper
   * can enforce the trial + license gate without an outbound backend call.
   */
  putBillingStatus(payload: HelperBillingStatusPayload): Promise<void>;
  getBillingStatus(): Promise<HelperBillingStatus>;
  /**
   * Push the user's Auth0 access token + the backend URL to the helper
   * so its outbound cloud-sync calls have valid credentials. Called
   * after auth + on every token refresh.
   */
  putCloudCredentials(payload: HelperCloudCredentialsPayload): Promise<void>;
  getCloudCredentials(): Promise<HelperCloudCredentialsStatus>;
  /**
   * Stream a question through the orchestrator. Invokes `onEvent` for
   * every server-sent event. Resolves once the stream closes (after `done`
   * or `error`) or when `signal` aborts. Always cancellable.
   */
  askStream(
    req: AskRequest,
    onEvent: (ev: AnswerEvent) => void,
    signal?: AbortSignal,
  ): Promise<void>;
}

export function createHelperClient(opts: HelperClientOptions): HelperClient {
  const fetchImpl = opts.fetchImpl ?? fetch;
  // Mutable so auto-rediscovery can update it after a stale-port recovery.
  let currentBaseUrl = opts.baseUrl;

  function buildHeaders(init: RequestInit): Headers {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${opts.bearer}`);
    const method = (init.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      headers.set('X-AIAssist-CSRF', opts.csrfToken);
    }
    if (init.body !== undefined && !headers.has('content-type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  }

  /**
   * Issue a request, recovering from a stale ephemeral port by calling
   * `/v1/discover` once if the underlying fetch throws a network error.
   * Returns the eventual `Response`; doesn't drain the body. Used by both
   * the JSON request helper and the SSE stream helper.
   */
  async function fetchWithRediscover(path: string, init: RequestInit): Promise<Response> {
    const url = (base: string) => `${base}${path}`;
    try {
      return await fetchImpl(url(currentBaseUrl), {
        ...init,
        headers: buildHeaders(init),
      });
    } catch (firstErr) {
      if (!isNetworkError(firstErr)) throw firstErr;
      // Try to recover: re-discover and retry once. If discovery itself
      // fails, surface the ORIGINAL error so callers see the actionable
      // "helper not reachable" diagnosis, not a confusing nested one.
      let discovered: DiscoveryResponse;
      try {
        discovered = await discoverHelper('auto-rediscover', fetchImpl);
      } catch {
        throw firstErr;
      }
      const newBase = `http://127.0.0.1:${discovered.port}`;
      if (newBase === currentBaseUrl) {
        // Port hasn't moved; the failure is something else (firewall,
        // helper crashed between discover and the actual call, …).
        throw firstErr;
      }
      currentBaseUrl = newBase;
      opts.onPortChanged?.(discovered.port);
      return fetchImpl(url(currentBaseUrl), {
        ...init,
        headers: buildHeaders(init),
      });
    }
  }

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetchWithRediscover(path, init);
    if (!res.ok) throw await asHelperError(res);
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('json')) return (await res.json()) as T;
    return undefined as T;
  }

  return {
    status: () => request<HelperStatus>('/v1/status'),
    listDirectories: () => request<DirectoriesResponse>('/v1/directories'),
    registerDirectory: (req) =>
      request<IndexDirectoryResponse>('/v1/directories', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    deleteDirectory: (id) =>
      request<void>(`/v1/directories/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    reindexDirectory: (id) =>
      request<IndexDirectoryResponse>(`/v1/directories/${encodeURIComponent(id)}/index`, {
        method: 'POST',
        body: '{}',
      }),
    search: (req) =>
      request<SearchResponse>('/v1/search', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    excerpt: (fileId, chunkId) =>
      request<ExcerptResponse>(
        `/v1/files/${encodeURIComponent(fileId)}/excerpt?chunk_id=${encodeURIComponent(chunkId)}`,
      ),
    ask: (req) =>
      request<AnswerResponse>('/v1/answer', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    putBillingStatus: (payload) =>
      request<void>('/v1/billing/status', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    getBillingStatus: () => request<HelperBillingStatus>('/v1/billing/status'),
    putCloudCredentials: (payload) =>
      request<void>('/v1/cloud/credentials', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    getCloudCredentials: () =>
      request<HelperCloudCredentialsStatus>('/v1/cloud/credentials'),
    listTranscripts: () => request<TranscriptListResponse>('/v1/transcripts'),
    getTranscript: (id) => request<TranscriptResponse>(`/v1/transcripts/${encodeURIComponent(id)}`),
    deleteTranscript: (id) =>
      request<void>(`/v1/transcripts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    summarizeTranscript: (id, force = false) =>
      request<TranscriptSummaryResponse>(`/v1/transcripts/${encodeURIComponent(id)}/summarize`, {
        method: 'POST',
        body: JSON.stringify({ force }),
      }),
    askStream: async (req, onEvent, signal) => {
      const init: RequestInit = {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        body: JSON.stringify(req),
      };
      if (signal) init.signal = signal;
      const res = await fetchWithRediscover('/v1/answer/stream', init);
      if (!res.ok) throw await asHelperError(res);
      if (!res.body) {
        throw new HelperError(res.status, null, 'response had no body');
      }
      await consumeSse<AnswerEvent>(res.body, onEvent);
    },
  };
}

/**
 * Minimal SSE consumer: reads the response body, parses `data:` lines into
 * JSON, and forwards each parsed payload to `onEvent`. Stops when the
 * stream closes naturally or the underlying reader aborts.
 */
async function consumeSse<T>(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: T) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Split on the SSE event terminator (\n\n). The leftover after the last
    // boundary stays in buf for the next read.
    const parts = buf.split('\n\n');
    buf = parts.pop() ?? '';
    for (const part of parts) {
      const dataLine = part.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const json = dataLine.slice(5).trimStart();
      if (!json) continue;
      try {
        const parsed = JSON.parse(json) as T;
        onEvent(parsed);
      } catch {
        // Ignore malformed events rather than killing the stream.
      }
    }
  }
}

/**
 * True if `e` is the browser's generic network failure (DNS, TCP refused,
 * CORS-rejected, or aborted). Used by `fetchWithRediscover` to decide
 * whether an error is recoverable by re-running discovery.
 *
 * Excludes `AbortError` so caller-initiated cancellations don't trigger a
 * pointless rediscovery retry.
 */
function isNetworkError(e: unknown): boolean {
  if (!(e instanceof TypeError)) return false;
  const name = (e as Error & { name?: string }).name;
  if (name === 'AbortError') return false;
  return /failed to fetch|load failed|networkerror/i.test(e.message ?? '');
}

async function asHelperError(res: Response): Promise<HelperError> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('problem+json') || ct.includes('json')) {
    try {
      const problem = (await res.json()) as HelperProblem;
      return new HelperError(res.status, problem);
    } catch {
      // fall through
    }
  }
  return new HelperError(res.status, null, `${res.status} ${res.statusText}`);
}
